import { debounce } from '@mui/material';
import {
 GridColDef,
 GridColumnOrderChangeParams,
} from '@mui/x-data-grid-premium';
import {
 MutableRefObject,
 useCallback,
 useEffect,
 useMemo,
 useRef,
 useState,
} from 'react';
import { useSelector } from 'react-redux';

import {
 getConfigStorageItem,
 setConfigStorageItem,
} from '../../../../hooks/useConfigStorage';
import { useCurrentStructure } from '../../../../hooks/useCurrentStructure';
import {
 changeCurrentPreset,
 ColPresetType,
 PageNameType,
} from '../../../../redux/clientConfiguration/clientConfigurationSlice';
import { findStructure } from '../../../../utils/dataTypes';
import { useAppDispatch } from '../../../../redux/hooks';
import { RootState } from '../../../../redux/store';
import {
 COLUMN_RESIZE_DEBOUNCE_MS,
 DEFAULT_COL_MAX_WIDTH,
 DEFAULT_PRESET_NAME,
 UNCAPPED_COL_MAX_WIDTH,
} from '../constants';
import { renderColWithStructure } from '../renderCol';
import { CustomGridApi } from '../types';
import { updateUrlFilterParams } from '../urlFilterParams';

interface ColumnResizeParams {
 colDef: { field: string };
 width: number;
}

interface PresetColumn {
 field: string;
 width?: number;
}

const applySavedColumnMaxWidth = (col: GridColDef): GridColDef => {
 const width = typeof col.width === 'number' ? col.width : undefined;
 if (width === undefined || width <= DEFAULT_COL_MAX_WIDTH) {
  return col;
 }
 return { ...col, maxWidth: UNCAPPED_COL_MAX_WIDTH };
};

const useColumnPresets = (
 pageName: string,
 apiRef: MutableRefObject<CustomGridApi>,
) => {
 const dispatch = useAppDispatch();
 const structure = useCurrentStructure();

 const currentColumnsPreset = useSelector(
  (state: RootState) =>
   state.clientConfiguration.currentColumnsPreset?.[pageName] || null,
 );
 const presetsColumns = useSelector(
  (state: RootState) => state.clientConfiguration.presetsColumns,
 );
 const tempColumnsPreset = useSelector(
  (state: RootState) =>
   state.clientConfiguration.tempColumnsPreset?.[pageName] || null,
 );
 const activePreset = tempColumnsPreset ?? currentColumnsPreset;

 const [columnsState, setColumnsState] = useState<GridColDef[]>([]);
 const activePresetRef = useRef(activePreset);
 activePresetRef.current = activePreset;

 useEffect(() => {
  const firstPreset = presetsColumns?.[pageName]?.[0];
  if (!firstPreset || currentColumnsPreset) return;

  const lsPreset = getConfigStorageItem(pageName)?.columns;
  if (lsPreset) {
   dispatch(
    changeCurrentPreset({
     page: pageName as PageNameType,
     type: 'columns',
     preset: lsPreset,
     temp: false,
    }),
   );
   return;
  }

  dispatch(
   changeCurrentPreset({
    page: pageName as PageNameType,
    type: 'columns',
    preset: firstPreset,
    temp: false,
   }),
  );
  setConfigStorageItem({
   page: pageName as PageNameType,
   key: 'columns',
   value: firstPreset,
  });
 }, [presetsColumns, currentColumnsPreset, dispatch, pageName]);

 useEffect(() => {
  if (!structure?.length || !currentColumnsPreset) return;
  const colsObj = renderColWithStructure(
   currentColumnsPreset.columns,
   structure,
  ).map(applySavedColumnMaxWidth);
  setColumnsState(colsObj);
 }, [currentColumnsPreset, structure]);

 const savePresetChange = useCallback(
  (
   newPreset: Partial<ColPresetType> & {
    name: string;
    columns: PresetColumn[];
   },
  ) => {
   setConfigStorageItem({
    page: pageName as PageNameType,
    key: 'columns',
    value: newPreset,
   });
   dispatch(
    changeCurrentPreset({
     page: pageName as PageNameType,
     type: 'columns',
     preset: newPreset,
     temp: true,
    }),
   );
  },
  [pageName, dispatch],
 );

 const persistColumnResize = useMemo(
  () =>
   debounce((params: ColumnResizeParams) => {
    const preset = activePresetRef.current;
    savePresetChange({
     ...preset,
     name: preset?.name || DEFAULT_PRESET_NAME,
     columns: preset?.columns
      ? preset.columns.map((col: PresetColumn) =>
         col.field === params.colDef.field
          ? { ...col, width: Math.round(params.width) }
          : col,
        )
      : [],
    });
   }, COLUMN_RESIZE_DEBOUNCE_MS),
  [savePresetChange],
 );

 useEffect(() => () => persistColumnResize.clear(), [persistColumnResize]);

 const handleColumnResizeStart = useCallback(
  (params: { field: string }) => {
   const column = apiRef.current?.getColumn(params.field);
   if (column) {
    column.maxWidth = UNCAPPED_COL_MAX_WIDTH;
   }
   setColumnsState((prevState) =>
    prevState.map((col) =>
     col.field === params.field
      ? { ...col, maxWidth: UNCAPPED_COL_MAX_WIDTH }
      : col,
    ),
   );
  },
  [apiRef],
 );

 useEffect(() => {
  if (!apiRef.current) return;
  return apiRef.current.subscribeEvent(
   'columnResizeStart',
   handleColumnResizeStart,
  );
 }, [apiRef, handleColumnResizeStart]);

 const handleColumnResize = useCallback(
  (params: ColumnResizeParams) => {
   setColumnsState((prevState) =>
    prevState.map((col) =>
     col.field === params.colDef.field
      ? {
         ...col,
         width: params.width,
         maxWidth: UNCAPPED_COL_MAX_WIDTH,
        }
      : col,
    ),
   );
   persistColumnResize(params);
  },
  [persistColumnResize],
 );

 const handleColumnOrderChange = useCallback(
  (params: GridColumnOrderChangeParams) => {
   setColumnsState((prevColumns) => {
    const { column } = params;
    const matchIndex = prevColumns.findIndex(
     (col) => col.field === column.field,
    );
    if (matchIndex === -1) return prevColumns;

    const updatedColumns = [...prevColumns];
    updatedColumns.splice(params.oldIndex - 1, 1);
    updatedColumns.splice(params.targetIndex - 1, 0, column);

   const preset = activePresetRef.current;
   savePresetChange({
    ...preset,
    name: preset?.name || DEFAULT_PRESET_NAME,
    columns: updatedColumns.map((col) => ({
     field: col.field,
     width: typeof col.width === 'number' ? Math.round(col.width) : col.width,
    })),
   });

    return updatedColumns;
   });
  },
  [savePresetChange],
 );

 const handleRemoveColumn = useCallback(
  (field: string) => {
   const preset = activePresetRef.current;
   if (!preset) return;

   const newColumns = preset.columns.filter((col: PresetColumn) => col.field !== field);

   let newFields = Object.fromEntries(
    Object.entries(preset.filters?.fields || {}).filter(([k]) => k !== field),
   );
   let newOperators = Object.fromEntries(
    Object.entries(preset.filters?.operators || {}).filter(([k]) => k !== field),
   );

   if (findStructure(structure, field)?.Type === 'datetime') {
    const dateCols = ['DateField', 'DateFrom', 'DateTo'];
    newFields = Object.fromEntries(
     Object.entries(newFields).filter(([k]) => !dateCols.includes(k)),
    );
    newOperators = Object.fromEntries(
     Object.entries(newOperators).filter(([k]) => !dateCols.includes(k)),
    );
   }

   const newPreset = {
    ...preset,
    columns: newColumns,
    filters: { fields: newFields, operators: newOperators },
   };

   dispatch(
    changeCurrentPreset({
     page: pageName as PageNameType,
     type: 'columns',
     preset: newPreset,
     temp: true,
    }),
   );
   dispatch(
    changeCurrentPreset({
     page: pageName as PageNameType,
     type: 'columns',
     preset: newPreset,
     temp: false,
    }),
   );
   setConfigStorageItem({
    page: pageName as PageNameType,
    key: 'columns',
    value: newPreset,
   });

   updateUrlFilterParams(newPreset.filters ?? null);

   const currentModel = apiRef.current.state.filter.filterModel;
   apiRef.current.setFilterModel({
    ...currentModel,
    items: currentModel.items.filter((item) => item.field !== field),
   });
  },
  [apiRef, dispatch, pageName, structure],
 );

 return {
  columnsState,
  currentColumnsPreset,
  presetsColumns,
  structure,
  handleColumnResize,
  handleColumnOrderChange,
  handleRemoveColumn,
 };
};

export default useColumnPresets;

